<script>
  import { router } from '@inertiajs/svelte'

  // Use-up modal. SEMANTICS: gray dot = available (unused), green dot = used.
  // Tap a gray dot to use one up; tap a green dot to add one back. Changes are
  // held locally and persisted only when "Save" is pressed (one PATCH).
  let {
    open = false,
    item,                 // { id, name, initial_quantity, remaining_quantity, property_id }
    onClose = () => {}
  } = $props()

  const COLS = 10           // dots per row
  const WINDOW_ROWS = 5     // rows shown when collapsed
  const RESERVED_GREEN = 8  // used (green) dots shown as a strip in the capped view
  const WINDOW = COLS * WINDOW_ROWS

  const initial = $derived(item?.initial_quantity ?? 1)

  // Local working copy of "remaining"; resets whenever the modal opens.
  let remaining = $state(item?.remaining_quantity ?? 1)
  let expanded = $state(false)
  let saving = $state(false)

  // Reset working state each time the modal is opened for an item.
  $effect(() => {
    if (open) {
      remaining = item?.remaining_quantity ?? 1
      expanded = false
    }
  })

  const used = $derived(Math.max(0, initial - remaining))
  const dirty = $derived(remaining !== (item?.remaining_quantity ?? 1))
  const capped = $derived(!expanded && initial > WINDOW)

  // Build the dot list. Available (gray) come first (they're the tap targets),
  // then used (green). When capped we show a window of available plus a strip
  // of used, so there's always something to tap.
  const cells = $derived.by(() => {
    const list = []
    if (!capped) {
      for (let i = 0; i < remaining; i++) list.push({ used: false })
      for (let i = 0; i < used; i++) list.push({ used: true })
    } else {
      const availToShow = Math.max(0, Math.min(remaining, WINDOW - RESERVED_GREEN))
      for (let i = 0; i < availToShow; i++) list.push({ used: false })
      const usedToShow = Math.min(used, RESERVED_GREEN)
      for (let i = 0; i < usedToShow; i++) list.push({ used: true })
    }
    return list
  })

  const lastRowStart = $derived(
    cells.length - (cells.length % COLS === 0 ? COLS : cells.length % COLS)
  )
  const cols = $derived(Math.min(COLS, Math.max(1, initial)))

  function tap(cell) {
    if (cell.used) remaining = Math.min(initial, remaining + 1)
    else remaining = Math.max(0, remaining - 1)
  }

  function dotOpacity(i, cell) {
    if (!capped || i < lastRowStart || cell.used) return 1
    return (1 - 0.16 * (i - lastRowStart)).toFixed(2)
  }

  function close() {
    if (saving) return
    onClose()
  }

  function save() {
    if (!dirty || !item) { onClose(); return }
    saving = true
    router.patch(
      `/properties/${item.property_id}/items/${item.id}/update_quantity`,
      { item: { remaining_quantity: remaining } },
      {
        preserveScroll: true,
        onSuccess: () => onClose(),
        onFinish: () => { saving = false }
      }
    )
  }
</script>

{#if open}
  <!-- Backdrop -->
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-5 bg-gray-900/55 backdrop-blur-[2px]"
    onclick={(e) => { if (e.target === e.currentTarget) close() }}
    role="presentation"
  >
    <div class="bg-white rounded-[20px] w-full max-w-[440px] shadow-2xl overflow-hidden">
      <!-- Head -->
      <div class="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <div>
          <div class="text-base font-extrabold text-gray-900">{item?.name}</div>
          <div class="text-[13px] text-gray-500 mt-0.5">Tap a dot to use it up or add it back</div>
        </div>
        <button
          onclick={close}
          class="w-[30px] h-[30px] rounded-lg grid place-items-center border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
          aria-label="Close"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
        </button>
      </div>

      <!-- Body -->
      <div class="p-5">
        <div class="flex items-baseline gap-2.5">
          <span class="text-[40px] font-extrabold text-gray-900 leading-none">{remaining}</span>
          <span class="text-sm text-gray-500">available of {initial}</span>
          <span class="ml-auto text-[13px] text-gray-500">Used <b class="text-gray-800">{used}</b></span>
        </div>

        <!-- bar fills with USED (green) to match the dots -->
        <div class="h-2 rounded-full bg-gray-100 overflow-hidden my-3">
          <div class="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-600" style="width: {initial ? (used / initial) * 100 : 0}%"></div>
        </div>

        <div
          class="grid gap-[9px] p-0.5 {expanded ? 'max-h-[260px] overflow-y-auto' : ''}"
          style="grid-template-columns: repeat({cols}, minmax(0, 1fr));"
        >
          {#each cells as cell, i}
            <button
              type="button"
              onclick={() => tap(cell)}
              title={cell.used ? 'Add one back' : 'Use one up'}
              style="opacity: {dotOpacity(i, cell)};"
              class="aspect-square rounded-full transition-transform active:scale-95 hover:scale-110 {cell.used
                ? 'bg-brand-500 shadow-[inset_0_0_0_1px_var(--color-brand-600)]'
                : 'bg-white shadow-[inset_0_0_0_1.5px_var(--gray-300,#d1d5db)]'}"
              aria-label={cell.used ? 'Used unit, tap to restore' : 'Available unit, tap to use'}
            ></button>
          {/each}
        </div>

        {#if initial > WINDOW}
          <div class="flex justify-center mt-3.5">
            <button
              type="button"
              onclick={() => (expanded = !expanded)}
              class="text-[13px] font-semibold text-brand-700 bg-brand-50 border border-brand-200 rounded-full px-4 py-1.5 hover:bg-brand-100"
            >
              {expanded ? 'Show less' : `Show all ${initial}`}
            </button>
          </div>
        {/if}

        <p class="mt-4 text-[12.5px] text-gray-400 text-center">
          {#if capped}
            Showing {cells.length} of {initial}. Tap a gray dot to use one up — “Show all” to see every unit.
          {:else}
            Gray = available, green = used. Tap to use up or add back.
          {/if}
        </p>
      </div>

      <!-- Foot -->
      <div class="flex justify-end gap-2.5 px-5 py-3.5 border-t border-gray-100">
        <button
          type="button"
          onclick={close}
          class="text-sm font-semibold px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onclick={save}
          disabled={saving}
          class="text-sm font-semibold px-4 py-2.5 rounded-lg bg-brand-700 text-white hover:bg-brand-800 disabled:opacity-60"
        >
          {saving ? 'Saving…' : dirty ? 'Save' : 'Done'}
        </button>
      </div>
    </div>
  </div>
{/if}
